import { EventType, Purpose, SchedulerScale } from "config/constants"
import dayjs, { Dayjs } from "utils/dayjs"
import { nowWithoutTimezone } from "utils/utils"

export function getCellWidthPercent(startDate: Dayjs, endDate: Dayjs, step: number, stepUnitOfTime: dayjs.ManipulateType): number {
    const cellsCount = Math.round(Math.abs(startDate.diff(endDate, stepUnitOfTime)) / step)
    return 100 / cellsCount
}

export function getDateByPercent(percent: number, startDate: Dayjs, endDate: Dayjs, step: number, stepUnitOfTime: dayjs.ManipulateType, roundUp: boolean = false): Dayjs {
    const startEndDiff = endDate.diff(startDate, stepUnitOfTime)
    const resultDateOffset = startEndDiff * percent / 100
    let date = dayjs(startDate).add(resultDateOffset, stepUnitOfTime)
    date = date.minute(Math[roundUp ? "ceil" : "floor"](date.minute() / step) * step)
    return date.startOf(stepUnitOfTime)
}

export function getSchedulerDefaultPeriod(): [Dayjs, Dayjs] {
    return [
        nowWithoutTimezone().subtract(3, "months").startOf("day"),
        nowWithoutTimezone().add(6, "months").hour(23).minute(45).startOf("minute"),
    ]
}

/**
 * Compute a pixel-based minimum width for the timeline container depending on the selected scale.
 * This ensures a horizontal scrollbar is available when the total content exceeds the viewport.
 * Values can be tuned to UX preferences.
 */
export function getTimelineMinWidthPx(startDate: Dayjs, endDate: Dayjs, scale: SchedulerScale): number {
    const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n))
    switch (scale) {
        case SchedulerScale.minutes: {
            // Used for ranges < 6h; width ~ 2px per minute (max ~ 720px)
            const minutes = Math.max(1, endDate.diff(startDate, "minutes"))
            return minutes * 2
        }
        case SchedulerScale.hours: {
            // Used for ranges < 3 days; ~24px per hour
            const hours = Math.max(1, endDate.diff(startDate, "hours"))
            return hours * 24
        }
        case SchedulerScale.days: {
            // Used for ranges < 40 days; ~160px per day
            const days = Math.max(1, endDate.diff(startDate, "days"))
            return days * 160
        }
        case SchedulerScale.weeks: {
            // Used for ranges < ~26 weeks; ~200px per week
            const weeks = Math.max(1, endDate.diff(startDate, "weeks"))
            return weeks * 200
        }
        case SchedulerScale.months: {
            // Used for ranges < 12 months; ~240px per month
            const months = clamp(endDate.diff(startDate, "months"), 1, 120)
            return months * 240
        }
        default: { // quarters
            // ~360px per quarter
            const quarters = clamp(endDate.diff(startDate, "quarters"), 1, 80)
            return quarters * 360
        }
    }
}
export function getSchedulerEventTypeByReservationPurpose(purpose: Purpose): EventType {
    if (purpose === Purpose.sale) {
        return EventType.sale
    }

    if (purpose === Purpose.relocation) {
        return EventType.relocation
    }

    return EventType.reservation
}